import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowClockwise, X, MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowsClockwise, FloppyDisk,
} from "@phosphor-icons/react";

const ZOOM_MIN = 1;
const ZOOM_MAX = 5;
const ZOOM_STEP = 0.5;

/**
 * Fullscreen progression-photo lightbox with rotate + zoom + pan + save.
 *
 * - Rotate button (and `R` key) flips the image in 90° increments.
 * - Zoom in/out buttons (and mouse wheel) scale the image up to 5× with
 *   constant-screen-position panning when above 1×.
 * - Pinch-to-zoom on mobile via 2-finger touch (distance-based scale).
 * - Save button appears when adjustments differ from props; clicking calls
 *   `onSave({ rotation })` and closes.
 * - Esc closes. Click the dim backdrop dismisses (no-op if dragging).
 */
export default function PhotoLightbox({ src, alt, initialRotation = 0, onSave, onClose }) {
  const [rotation, setRotation] = useState(initialRotation);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const dragRef = useRef({ active: false, lastX: 0, lastY: 0, moved: false });
  const pinchRef = useRef({ active: false, startDist: 0, startZoom: 1 });

  const dirty = rotation !== initialRotation;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "r") setRotation((r) => (r + 90) % 360);
      else if (e.key === "R") setRotation((r) => (r + 270) % 360);
      else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
      else if (e.key === "-") setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));
      else if (e.key === "0") { setZoom(1); setPan({ x: 0, y: 0 }); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Reset pan when zooming back to 1× — otherwise the image stays offset.
  useEffect(() => { if (zoom === 1) setPan({ x: 0, y: 0 }); }, [zoom]);

  const sideways = rotation % 180 !== 0;

  const rotate = (e) => {
    e.stopPropagation();
    setRotation((r) => (r + 90) % 360);
  };

  const zoomIn = (e) => { e.stopPropagation(); setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP)); };
  const zoomOut = (e) => { e.stopPropagation(); setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP)); };
  const reset = (e) => { e.stopPropagation(); setZoom(1); setPan({ x: 0, y: 0 }); };

  const onWheel = (e) => {
    // Wheel up zooms in, wheel down zooms out. No preventDefault — passive listener.
    const delta = -Math.sign(e.deltaY) * ZOOM_STEP;
    setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta)));
  };

  const onMouseDown = (e) => {
    if (zoom <= 1) return;
    dragRef.current = { active: true, lastX: e.clientX, lastY: e.clientY, moved: false };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.lastX;
    const dy = e.clientY - dragRef.current.lastY;
    dragRef.current.lastX = e.clientX;
    dragRef.current.lastY = e.clientY;
    if (Math.abs(dx) + Math.abs(dy) > 2) dragRef.current.moved = true;
    setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
  };
  const onMouseUp = () => { dragRef.current.active = false; };

  // Touch handlers: 1 finger pans (when zoomed), 2 fingers pinch-zoom.
  const distOf = (touches) => {
    const [a, b] = touches;
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  };
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinchRef.current = { active: true, startDist: distOf(e.touches), startZoom: zoom };
    } else if (e.touches.length === 1 && zoom > 1) {
      const t = e.touches[0];
      dragRef.current = { active: true, lastX: t.clientX, lastY: t.clientY, moved: false };
    }
  };
  const onTouchMove = (e) => {
    if (pinchRef.current.active && e.touches.length === 2) {
      const d = distOf(e.touches);
      const ratio = d / pinchRef.current.startDist;
      const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, pinchRef.current.startZoom * ratio));
      setZoom(next);
    } else if (dragRef.current.active && e.touches.length === 1) {
      const t = e.touches[0];
      const dx = t.clientX - dragRef.current.lastX;
      const dy = t.clientY - dragRef.current.lastY;
      dragRef.current.lastX = t.clientX;
      dragRef.current.lastY = t.clientY;
      if (Math.abs(dx) + Math.abs(dy) > 2) dragRef.current.moved = true;
      setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
    }
  };
  const onTouchEnd = (e) => {
    if (e.touches.length < 2) pinchRef.current.active = false;
    if (e.touches.length === 0) dragRef.current.active = false;
  };

  const backdropClick = () => {
    // Suppress backdrop-close if we just panned past the image
    if (dragRef.current.moved) { dragRef.current.moved = false; return; }
    onClose();
  };

  const save = async (e) => {
    e.stopPropagation();
    if (!onSave || !dirty) { onClose(); return; }
    setSaving(true);
    try {
      await onSave({ rotation });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] bg-ink/90 backdrop-blur-sm flex items-center justify-center p-4 select-none"
      onClick={backdropClick}
      role="dialog"
      aria-modal="true"
      data-testid="photo-lightbox"
    >
      {/* Top toolbar */}
      <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between gap-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <ToolbarButton onClick={rotate} icon={ArrowClockwise} label="Rotate" testid="photo-lightbox-rotate" title="Rotate (R)" />
          <ToolbarButton onClick={zoomIn} icon={MagnifyingGlassPlus} label="Zoom in" testid="photo-lightbox-zoom-in" title="Zoom in (+)" disabled={zoom >= ZOOM_MAX} compact />
          <ToolbarButton onClick={zoomOut} icon={MagnifyingGlassMinus} label="Zoom out" testid="photo-lightbox-zoom-out" title="Zoom out (-)" disabled={zoom <= ZOOM_MIN} compact />
          <ToolbarButton onClick={reset} icon={ArrowsClockwise} label="Reset" testid="photo-lightbox-reset" title="Reset (0)" disabled={zoom === 1 && pan.x === 0 && pan.y === 0} compact />
          <span className="px-2 py-1 rounded-md bg-white/10 text-white/80 font-data text-[11px]" data-testid="photo-lightbox-zoom-value">
            {(zoom * 100).toFixed(0)}%
          </span>
        </div>
        <div className="flex items-center gap-2">
          {onSave && (
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-colors ${
                dirty && !saving
                  ? "bg-moss text-white hover:bg-moss/90"
                  : "bg-white/15 text-white/50 cursor-not-allowed"
              }`}
              data-testid="photo-lightbox-save"
            >
              <FloppyDisk size={14} weight="bold" />
              {saving ? "Saving…" : dirty ? "Save" : "Saved"}
            </button>
          )}
          <ToolbarButton onClick={onClose} icon={X} label="Close" testid="photo-lightbox-close" title="Close (Esc)" />
        </div>
      </div>

      {/* Image stage */}
      <div
        className={`flex items-center justify-center overflow-hidden ${sideways ? "max-w-[90vh] max-h-[90vw]" : "max-w-[90vw] max-h-[90vh]"}`}
        style={{ cursor: zoom > 1 ? "grab" : "default" }}
        onClick={(e) => e.stopPropagation()}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          src={src}
          alt={alt}
          className="drop-shadow-2xl"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) rotate(${rotation}deg) scale(${zoom})`,
            transition: dragRef.current.active || pinchRef.current.active ? "none" : "transform 200ms ease",
            maxWidth: sideways ? "90vh" : "90vw",
            maxHeight: sideways ? "90vw" : "90vh",
            touchAction: "none",
          }}
          draggable={false}
        />
      </div>
    </div>,
    document.body,
  );
}

function ToolbarButton({ onClick, icon: Icon, label, testid, title, disabled, compact }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={label}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 ${compact ? "p-2" : "px-3 py-2"} rounded-full text-white transition-colors text-sm font-semibold ${
        disabled
          ? "bg-white/5 text-white/40 cursor-not-allowed"
          : "bg-white/15 hover:bg-white/25"
      }`}
    >
      <Icon size={16} weight="bold" />
      {!compact && <span className="hidden sm:inline">{label}</span>}
    </button>
  );
}
